import React, { useEffect, useState } from "react";
import { Rocket, Notepad, ChartLineUp, Scales, Coins } from "@phosphor-icons/react";

/**
 * Sticky floating pill nav for long landing pages.
 * Highlights the section currently in view via IntersectionObserver.
 * Hidden on mobile so it doesn't crowd the hero.
 */
const SECTIONS = [
  { id: "prop-challenge",       label: "Prop Challenge", icon: Rocket },
  { id: "metatrader",           label: "MT4 / MT5",      icon: Notepad },
  { id: "features",             label: "Features",       icon: ChartLineUp },
  { id: "compare",              label: "Compare",        icon: Scales },
  { id: "pricing",              label: "Pricing",        icon: Coins },
];

export default function SectionNav() {
  const [active, setActive] = useState(SECTIONS[0].id);

  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      // Pick the entry closest to the top of the viewport that is currently
      // intersecting — that's the section the user is "reading".
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]?.target?.id) setActive(visible[0].target.id);
    }, { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.25, 0.5, 1] });
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  return (
    <nav
      data-testid="landing-section-nav"
      className="hidden lg:flex fixed left-1/2 -translate-x-1/2 bottom-6 z-40 items-center gap-1 rounded-full bg-neutral-900/90 backdrop-blur-xl border border-white/10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] p-1"
    >
      {SECTIONS.map((s) => {
        const Icon = s.icon;
        const isActive = active === s.id;
        return (
          <a key={s.id} href={`#${s.id}`}
             data-testid={`section-nav-${s.id}`}
             className={`h-9 px-3.5 rounded-full flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest transition-all ${
               isActive
                 ? "bg-white text-neutral-900 shadow"
                 : "text-white/70 hover:text-white hover:bg-white/10"
             }`}>
            <Icon size={12} weight={isActive ? "fill" : "bold"} />
            <span>{s.label}</span>
          </a>
        );
      })}
    </nav>
  );
}
